/**
 * Local Bridge MarketFit: connect product inputs to situation assets (exact/partial)
 * and product benefits to situation needs (exact/partial).
 * Includes Value conversion (core schema v2): emotional justifications, commitment decisions, situation deltas.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Card, Button, Select, Table, message, Spin, Typography, Collapse, Modal, InputNumber, Input, Checkbox } from 'antd';
import { AimOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getMarketFitDealDetail,
  getMarketFitDealScores,
  updateMarketFitDeal,
  listMarketFitActors,
  upsertEmotionalJustification,
  upsertCommitmentDecision,
  upsertSituationDelta,
  type MarketFitDealDetail,
  type MarketFitScores,
} from '../../services/localBridgeSessionApi';
import {
  parseProductInputsBenefits,
  parseSituationDetails,
  parseDealConnectionLinks,
  type ProductInputItem,
  type ProductBenefitItem,
  type SituationAssetItem,
  type SituationNeedItem,
  type DealConnectionLinks,
  type InputAssetLink,
  type BenefitNeedLink,
  CONNECTION_QUALIFIERS,
  qualifierToMatchType,
  matchTypeToQualifier,
  generateId,
} from '../../types/marketFitLocalBridge';

interface DealConnectionsDrawerProps {
  open: boolean;
  dealId: string | null;
  onClose: () => void;
  onSaved: () => void;
  variant?: 'drawer' | 'panel';
}

function ValueConversionSection({
  dealDetail,
  onOpenModal,
}: {
  dealDetail: MarketFitDealDetail | null;
  onOpenModal: (m: 'justification' | 'decision' | 'situation_delta') => void;
}) {
  return (
    <>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Emotional justifications, commitment decisions, and situation deltas for this deal.
      </Typography.Text>
      {(dealDetail?.emotional_justifications?.length ?? 0) + (dealDetail?.commitment_decisions?.length ?? 0) > 0 && (
        <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
          Justifications: {(dealDetail?.emotional_justifications?.filter((j) => j.justification_pass).length ?? 0)} / {(dealDetail?.emotional_justifications?.length ?? 0)} pass
          {' · '}
          Decisions: {(dealDetail?.commitment_decisions?.filter((d) => d.decision_pass).length ?? 0)} / {(dealDetail?.commitment_decisions?.length ?? 0)} pass
        </Typography.Text>
      )}
      <Typography.Title level={5}>Emotional justifications</Typography.Title>
      <Table
        size="small"
        pagination={false}
        dataSource={dealDetail?.emotional_justifications ?? []}
        rowKey={(r) => `${r.deal_id}-${r.actor_id}`}
        columns={[
          { title: 'Actor ID', dataIndex: 'actor_id', key: 'actor_id', width: 120 },
          { title: 'Value score', dataIndex: 'emotional_value_score', key: 'ev', width: 90 },
          { title: 'Cost score', dataIndex: 'emotional_cost_score', key: 'ec', width: 90 },
          { title: 'Identity align', dataIndex: 'identity_alignment_score', key: 'ia', width: 90 },
          { title: 'Pass', dataIndex: 'justification_pass', key: 'pass', width: 60, render: (v) => (v ? 'Yes' : 'No') },
        ]}
      />
      <Button type="dashed" icon={<PlusOutlined />} size="small" onClick={() => onOpenModal('justification')} style={{ marginTop: 8 }}>Add justification</Button>
      <Typography.Title level={5} style={{ marginTop: 16 }}>Commitment decisions</Typography.Title>
      <Table
        size="small"
        pagination={false}
        dataSource={dealDetail?.commitment_decisions ?? []}
        rowKey={(r) => `${r.deal_id}-${r.actor_id}-${r.situation_id}-${r.product_id}`}
        columns={[
          { title: 'Actor', dataIndex: 'actor_id', key: 'actor_id', width: 100 },
          { title: 'Situation', dataIndex: 'situation_id', key: 'situation_id', width: 100, ellipsis: true },
          { title: 'Product', dataIndex: 'product_id', key: 'product_id', width: 100, ellipsis: true },
          { title: 'Amount', dataIndex: 'authorized_amount', key: 'amount', width: 80 },
          { title: 'Timing', dataIndex: 'timing', key: 'timing', width: 80 },
          { title: 'Pass', dataIndex: 'decision_pass', key: 'pass', width: 60, render: (v) => (v ? 'Yes' : 'No') },
        ]}
      />
      <Button type="dashed" icon={<PlusOutlined />} size="small" onClick={() => onOpenModal('decision')} style={{ marginTop: 8 }}>Add decision</Button>
      <Typography.Title level={5} style={{ marginTop: 16 }}>Situation deltas</Typography.Title>
      <Table
        size="small"
        pagination={false}
        dataSource={dealDetail?.situation_deltas ?? []}
        rowKey={(r) => `${r.deal_id}-${r.situation_id}`}
        columns={[
          { title: 'Situation ID', dataIndex: 'situation_id', key: 'situation_id', width: 120 },
          { title: 'Deltas', dataIndex: 'deltas_json', key: 'deltas', ellipsis: true, render: (t) => (t?.length > 60 ? t.slice(0, 60) + '…' : t) },
        ]}
      />
      <Button type="dashed" icon={<PlusOutlined />} size="small" onClick={() => onOpenModal('situation_delta')} style={{ marginTop: 8 }}>Add situation delta</Button>
    </>
  );
}

export const DealConnectionsDrawer: React.FC<DealConnectionsDrawerProps> = ({
  open,
  dealId,
  onClose,
  onSaved,
  variant = 'drawer',
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dealName, setDealName] = useState('');
  const [inputsByProduct, setInputsByProduct] = useState<{ productId: string; productName: string; inputs: ProductInputItem[] }[]>([]);
  const [benefitsByProduct, setBenefitsByProduct] = useState<{ productId: string; productName: string; benefits: ProductBenefitItem[] }[]>([]);
  const [assetsBySituation, setAssetsBySituation] = useState<{ situationId: string; situationName: string; assets: SituationAssetItem[] }[]>([]);
  const [needsBySituation, setNeedsBySituation] = useState<{ situationId: string; situationName: string; needs: SituationNeedItem[] }[]>([]);
  const [inputLinks, setInputLinks] = useState<InputAssetLink[]>([]);
  const [benefitLinks, setBenefitLinks] = useState<BenefitNeedLink[]>([]);
  const [dealDetail, setDealDetail] = useState<MarketFitDealDetail | null>(null);
  const [scores, setScores] = useState<MarketFitScores | null>(null);
  const [valueConvModal, setValueConvModal] = useState<'justification' | 'decision' | 'situation_delta' | null>(null);
  const [valueConvSaving, setValueConvSaving] = useState(false);
  const [justifForm, setJustifForm] = useState({ actorId: '', emotional_value_score: 0, emotional_cost_score: 0, identity_alignment_score: 0, justification_pass: false });
  const [decisionForm, setDecisionForm] = useState({ actorId: '', situationId: '', productId: '', authorized_amount: 0, timing: 'immediate' as const, reversibility: 0, decision_pass: false });
  const [situationDeltaForm, setSituationDeltaForm] = useState({ situationId: '', deltasJson: '{}' });
  const [actorOptions, setActorOptions] = useState<{ value: string; label: string }[]>([]);

  const loadDeal = useCallback(async () => {
    if (!dealId) return;
    if (variant === 'drawer' && !open) return;
    setLoading(true);
    try {
      const [detail, scoresRes] = await Promise.all([
        getMarketFitDealDetail(dealId),
        getMarketFitDealScores(dealId),
      ]);
      if (detail) {
        setDealDetail(detail);
        setDealName(detail.name);
      }
      setScores(scoresRes ?? null);
      if (detail) {
        const products = detail.products || [];
        const situations = detail.situations || [];
        setInputsByProduct(
          products.map((p) => ({
            productId: p.id,
            productName: p.name,
            inputs: parseProductInputsBenefits(p.inputs_benefits_json).inputs,
          }))
        );
        setBenefitsByProduct(
          products.map((p) => ({
            productId: p.id,
            productName: p.name,
            benefits: parseProductInputsBenefits(p.inputs_benefits_json).benefits,
          }))
        );
        setAssetsBySituation(
          situations.map((s) => ({
            situationId: s.id,
            situationName: s.name,
            assets: parseSituationDetails(s.details_json).assets,
          }))
        );
        setNeedsBySituation(
          situations.map((s) => ({
            situationId: s.id,
            situationName: s.name,
            needs: parseSituationDetails(s.details_json).needs,
          }))
        );
        const links = parseDealConnectionLinks(detail.connection_links_json);
        setInputLinks(links.solutionInputToSituationResourceLinks);
        setBenefitLinks(links.solutionBenefitToSituationNeedLinks);
      }
    } finally {
      setLoading(false);
    }
  }, [open, dealId, variant]);

  useEffect(() => {
    loadDeal();
  }, [loadDeal]);

  const getInputLink = (productId: string, inputId: string) =>
    inputLinks.find((l) => l.fromId === inputId && (l.productId === productId || !l.productId));
  const setInputLink = (productId: string, situationId: string, inputId: string, assetId: string | null, qualifier: 'exact' | 'partial') => {
    setInputLinks((prev) => {
      const rest = prev.filter((l) => !(l.fromId === inputId && (l.productId === productId || !l.productId)));
      if (!assetId) return rest;
      const existing = prev.find((l) => l.fromId === inputId);
      return [
        ...rest,
        {
          id: existing?.id ?? generateId(),
          fromId: inputId,
          toId: assetId,
          matchType: qualifierToMatchType(qualifier),
          productId: productId,
          situationId: situationId,
        },
      ];
    });
  };

  const getBenefitLink = (productId: string, benefitId: string) =>
    benefitLinks.find((l) => l.fromId === benefitId && (l.productId === productId || !l.productId));
  const setBenefitLink = (productId: string, situationId: string, benefitId: string, needId: string | null, qualifier: 'exact' | 'partial') => {
    setBenefitLinks((prev) => {
      const rest = prev.filter((l) => !(l.fromId === benefitId && (l.productId === productId || !l.productId)));
      if (!needId) return rest;
      const existing = prev.find((l) => l.fromId === benefitId);
      return [
        ...rest,
        {
          id: existing?.id ?? generateId(),
          fromId: benefitId,
          toId: needId,
          matchType: qualifierToMatchType(qualifier),
          productId: productId,
          situationId: situationId,
        },
      ];
    });
  };

  const handleSave = async () => {
    if (!dealId) return;
    setSaving(true);
    try {
      const payload = {
        connection_links_json: JSON.stringify({
          solutionInputToSituationResourceLinks: inputLinks,
          solutionBenefitToSituationNeedLinks: benefitLinks,
        }),
      };
      const updated = await updateMarketFitDeal(dealId, payload);
      if (updated) {
        message.success('Connections saved');
        onSaved();
      } else {
        message.error('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const allAssets = assetsBySituation.flatMap((s) => s.assets.map((a) => ({ ...a, situationId: s.situationId, situationName: s.situationName })));
  const allNeeds = needsBySituation.flatMap((s) => s.needs.map((n) => ({ ...n, situationId: s.situationId, situationName: s.situationName })));
  const assetOptions = allAssets.map((a) => ({
    value: a.id,
    label: `${a.name} (${a.situationName})`,
    situationId: a.situationId,
  }));
  const needOptions = allNeeds.map((n) => ({
    value: n.id,
    label: `${n.name} (${n.situationName})`,
    situationId: n.situationId,
  }));

  const footer = (
    <>
      <Button onClick={onClose} icon={variant === 'panel' ? <CloseOutlined /> : undefined}>Close</Button>
      <Button type="primary" loading={saving} onClick={handleSave}>Save connections</Button>
    </>
  );

  const content = (
    <Spin spinning={loading}>
        {!dealId ? (
          <Typography.Text type="secondary">Select a deal to define connections.</Typography.Text>
        ) : (
          <>
            <Typography.Title level={5}>Product inputs → Situation assets (exact or partial)</Typography.Title>
            <p style={{ color: '#666', marginBottom: 12 }}>
              Connect each product input to a situation asset and choose whether the match is exact or partial.
            </p>
            {inputsByProduct.map(({ productId, productName, inputs }) => (
              <div key={productId} style={{ marginBottom: 24 }}>
                <Typography.Text strong>Product: {productName}</Typography.Text>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={inputs}
                  rowKey="id"
                  columns={[
                    { title: 'Input', dataIndex: 'name', key: 'name', width: 180 },
                    {
                      title: 'Asset',
                      key: 'asset',
                      render: (_, r) => {
                        const link = getInputLink(productId, r.id);
                        const opt = assetOptions.find((o) => o.value === link?.toId);
                        return (
                          <Select
                            allowClear
                            placeholder="Select asset"
                            value={link?.toId ?? undefined}
                            onChange={(assetId) => {
                              const o = assetOptions.find((x) => x.value === assetId);
                              if (o) setInputLink(productId, o.situationId, r.id, assetId, link?.matchType === 'full' ? 'exact' : 'partial');
                            }}
                            onClear={() => setInputLink(productId, '', r.id, null, 'exact')}
                            options={assetOptions}
                            style={{ width: '100%', minWidth: 160 }}
                          />
                        );
                      },
                    },
                    {
                      title: 'Qualifier',
                      width: 110,
                      render: (_, r) => {
                        const link = getInputLink(productId, r.id);
                        const qualifier = link ? matchTypeToQualifier(link.matchType) : 'exact';
                        return (
                          <Select
                            value={qualifier}
                            onChange={(q) => {
                              if (link?.toId) {
                                const o = assetOptions.find((x) => x.value === link.toId);
                                const sid = o?.situationId ?? link.situationId ?? '';
                                setInputLink(productId, sid, r.id, link.toId, q);
                              }
                            }}
                            options={CONNECTION_QUALIFIERS.map((c) => ({ value: c, label: c }))}
                            style={{ width: '100%' }}
                          />
                        );
                      },
                    },
                  ]}
                />
              </div>
            ))}

            <Typography.Title level={5} style={{ marginTop: 24 }}>Product benefits → Situation needs (exact or partial)</Typography.Title>
            {benefitsByProduct.map(({ productId, productName, benefits }) => (
              <div key={productId} style={{ marginBottom: 24 }}>
                <Typography.Text strong>Product: {productName}</Typography.Text>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={benefits}
                  rowKey="id"
                  columns={[
                    { title: 'Benefit', dataIndex: 'name', key: 'name', width: 180 },
                    {
                      title: 'Need',
                      key: 'need',
                      render: (_, r) => {
                        const link = getBenefitLink(productId, r.id);
                        return (
                          <Select
                            allowClear
                            placeholder="Select need"
                            value={link?.toId ?? undefined}
                            onChange={(needId) => {
                              const o = needOptions.find((x) => x.value === needId);
                              if (o) setBenefitLink(productId, o.situationId, r.id, needId, link?.matchType === 'full' ? 'exact' : 'partial');
                            }}
                            onClear={() => setBenefitLink(productId, '', r.id, null, 'exact')}
                            options={needOptions}
                            style={{ width: '100%', minWidth: 160 }}
                          />
                        );
                      },
                    },
                    {
                      title: 'Qualifier',
                      width: 110,
                      render: (_, r) => {
                        const link = getBenefitLink(productId, r.id);
                        const qualifier = link ? matchTypeToQualifier(link.matchType) : 'exact';
                        return (
                          <Select
                            value={qualifier}
                            onChange={(q) => {
                              if (link?.toId) {
                                const o = needOptions.find((x) => x.value === link.toId);
                                const sid = o?.situationId ?? link.situationId ?? '';
                                setBenefitLink(productId, sid, r.id, link.toId, q);
                              }
                            }}
                            options={CONNECTION_QUALIFIERS.map((c) => ({ value: c, label: c }))}
                            style={{ width: '100%' }}
                          />
                        );
                      },
                    },
                  ]}
                />
              </div>
            ))}

            {(scores != null && (scores.svp != null || scores.acr != null || scores.ce != null || scores.marketfit_score != null)) && (
              <div style={{ marginTop: 24, marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>Scores</Typography.Title>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'baseline' }}>
                  {scores.svp != null && <Typography.Text>SVP: <strong>{scores.svp.toFixed(1)}</strong><span className="ant-typography ant-typography-secondary">/10</span></Typography.Text>}
                  {scores.acr != null && <Typography.Text>ACR: <strong>{scores.acr.toFixed(1)}</strong><span className="ant-typography ant-typography-secondary">/10</span></Typography.Text>}
                  {scores.ce != null && <Typography.Text>CE: <strong>{scores.ce.toFixed(1)}</strong><span className="ant-typography ant-typography-secondary">/10</span></Typography.Text>}
                  {scores.marketfit_score != null && <Typography.Text>MarketFit: <strong>{scores.marketfit_score.toFixed(0)}</strong><span className="ant-typography ant-typography-secondary">/100</span></Typography.Text>}
                  {scores.interpretation?.label && <Typography.Text type="secondary" style={{ marginLeft: 8 }}>{scores.interpretation.label}</Typography.Text>}
                </div>
              </div>
            )}

            <Collapse style={{ marginTop: 24 }} items={[
              {
                key: 'value_conversion',
                label: 'Value conversion (core schema v2)',
                children: <ValueConversionSection dealDetail={dealDetail} onOpenModal={setValueConvModal} />,
              },
            ]} />
          </>
        )}
    </Spin>
  );

  const handleUpsertJustification = async (actorId: string, emotional_value_score: number, emotional_cost_score: number, identity_alignment_score: number, justification_pass: boolean) => {
    if (!dealId) return;
    setValueConvSaving(true);
    try {
      const ok = await upsertEmotionalJustification(dealId, {
        deal_id: dealId,
        actor_id: actorId,
        emotional_value_score,
        emotional_cost_score,
        identity_alignment_score,
        justification_pass,
      });
      if (ok) { message.success('Justification saved'); setValueConvModal(null); loadDeal(); onSaved(); }
      else message.error('Failed to save');
    } finally {
      setValueConvSaving(false);
    }
  };
  const handleUpsertDecision = async (actorId: string, situationId: string, productId: string, authorized_amount: number, timing: string, reversibility: number, decision_pass: boolean) => {
    if (!dealId) return;
    setValueConvSaving(true);
    try {
      const ok = await upsertCommitmentDecision(dealId, {
        deal_id: dealId,
        actor_id: actorId,
        situation_id: situationId,
        product_id: productId,
        authorized_amount,
        timing,
        reversibility,
        decision_pass,
      });
      if (ok) { message.success('Commitment decision saved'); setValueConvModal(null); loadDeal(); onSaved(); }
      else message.error('Failed to save');
    } finally {
      setValueConvSaving(false);
    }
  };
  const handleUpsertSituationDelta = async (situationId: string, deltasJson: string) => {
    if (!dealId) return;
    setValueConvSaving(true);
    try {
      const ok = await upsertSituationDelta(dealId, { deal_id: dealId, situation_id: situationId, deltas_json: deltasJson });
      if (ok) { message.success('Situation delta saved'); setValueConvModal(null); loadDeal(); onSaved(); }
      else message.error('Failed to save');
    } finally {
      setValueConvSaving(false);
    }
  };

  React.useEffect(() => {
    if ((valueConvModal === 'justification' || valueConvModal === 'decision') && dealDetail?.company_id) {
      listMarketFitActors(dealDetail.company_id).then((actors) => {
        setActorOptions(actors.map((a) => ({ value: a.id, label: a.actor_name || a.id })));
      });
    }
  }, [valueConvModal, dealDetail?.company_id]);

  const valueConvModals = (
    <>
      <Modal
        title="Add emotional justification"
        open={valueConvModal === 'justification'}
        onCancel={() => setValueConvModal(null)}
        onOk={() => handleUpsertJustification(justifForm.actorId, justifForm.emotional_value_score, justifForm.emotional_cost_score, justifForm.identity_alignment_score, justifForm.justification_pass)}
        confirmLoading={valueConvSaving}
        okText="Save"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span>Actor</span>
          <Select
            placeholder="Select actor"
            value={justifForm.actorId || undefined}
            onChange={(v) => setJustifForm((f) => ({ ...f, actorId: v }))}
            options={actorOptions}
            style={{ width: '100%' }}
          />
          <span>Emotional value score</span>
          <InputNumber value={justifForm.emotional_value_score} onChange={(v) => setJustifForm((f) => ({ ...f, emotional_value_score: v ?? 0 }))} style={{ width: '100%' }} />
          <span>Emotional cost score</span>
          <InputNumber value={justifForm.emotional_cost_score} onChange={(v) => setJustifForm((f) => ({ ...f, emotional_cost_score: v ?? 0 }))} style={{ width: '100%' }} />
          <span>Identity alignment score</span>
          <InputNumber value={justifForm.identity_alignment_score} onChange={(v) => setJustifForm((f) => ({ ...f, identity_alignment_score: v ?? 0 }))} style={{ width: '100%' }} />
          <Checkbox checked={justifForm.justification_pass} onChange={(e) => setJustifForm((f) => ({ ...f, justification_pass: e.target.checked }))}>Justification pass</Checkbox>
        </div>
      </Modal>
      <Modal
        title="Add commitment decision"
        open={valueConvModal === 'decision'}
        onCancel={() => setValueConvModal(null)}
        onOk={() => handleUpsertDecision(decisionForm.actorId, decisionForm.situationId, decisionForm.productId, decisionForm.authorized_amount, decisionForm.timing, decisionForm.reversibility, decisionForm.decision_pass)}
        confirmLoading={valueConvSaving}
        okText="Save"
        width={480}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span>Actor</span>
          <Select placeholder="Select actor" value={decisionForm.actorId || undefined} onChange={(v) => setDecisionForm((f) => ({ ...f, actorId: v }))} options={actorOptions} style={{ width: '100%' }} />
          <span>Situation</span>
          <Select
            placeholder="Select situation"
            value={decisionForm.situationId || undefined}
            onChange={(v) => setDecisionForm((f) => ({ ...f, situationId: v }))}
            options={(dealDetail?.situations ?? []).map((s) => ({ value: s.id, label: s.name }))}
            style={{ width: '100%' }}
          />
          <span>Product</span>
          <Select
            placeholder="Select product"
            value={decisionForm.productId || undefined}
            onChange={(v) => setDecisionForm((f) => ({ ...f, productId: v }))}
            options={(dealDetail?.products ?? []).map((p) => ({ value: p.id, label: p.name }))}
            style={{ width: '100%' }}
          />
          <span>Authorized amount</span>
          <InputNumber value={decisionForm.authorized_amount} onChange={(v) => setDecisionForm((f) => ({ ...f, authorized_amount: v ?? 0 }))} style={{ width: '100%' }} />
          <span>Timing</span>
          <Select value={decisionForm.timing} onChange={(v) => setDecisionForm((f) => ({ ...f, timing: v }))} options={[{ value: 'immediate', label: 'Immediate' }, { value: 'delayed', label: 'Delayed' }]} style={{ width: '100%' }} />
          <span>Reversibility</span>
          <InputNumber value={decisionForm.reversibility} onChange={(v) => setDecisionForm((f) => ({ ...f, reversibility: v ?? 0 }))} style={{ width: '100%' }} />
          <Checkbox checked={decisionForm.decision_pass} onChange={(e) => setDecisionForm((f) => ({ ...f, decision_pass: e.target.checked }))}>Decision pass</Checkbox>
        </div>
      </Modal>
      <Modal
        title="Add situation delta"
        open={valueConvModal === 'situation_delta'}
        onCancel={() => setValueConvModal(null)}
        onOk={() => handleUpsertSituationDelta(situationDeltaForm.situationId, situationDeltaForm.deltasJson)}
        confirmLoading={valueConvSaving}
        okText="Save"
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span>Situation</span>
          <Select
            placeholder="Select situation"
            value={situationDeltaForm.situationId || undefined}
            onChange={(v) => setSituationDeltaForm((f) => ({ ...f, situationId: v }))}
            options={(dealDetail?.situations ?? []).map((s) => ({ value: s.id, label: s.name }))}
            style={{ width: '100%' }}
          />
          <span>Deltas (JSON)</span>
          <Input.TextArea rows={4} value={situationDeltaForm.deltasJson} onChange={(e) => setSituationDeltaForm((f) => ({ ...f, deltasJson: e.target.value }))} placeholder='{"updated_constraints":[],"updated_assets":[],"updated_currency_value_ratio":null}' />
        </div>
      </Modal>
    </>
  );

  if (variant === 'panel') {
    return (
      <>
        <Card
          title={<><AimOutlined /> Connections: {dealName || '…'}</>}
          extra={<Button type="text" icon={<CloseOutlined />} onClick={onClose} />}
          style={{ height: '100%' }}
        >
          {content}
          <div style={{ marginTop: 16 }}>{footer}</div>
        </Card>
        {valueConvModals}
      </>
    );
  }
  return (
    <>
      <Drawer
        title={<><AimOutlined /> Connections: {dealName || '…'}</>}
        open={open}
        onClose={onClose}
        width={720}
        footer={footer}
      >
        {content}
      </Drawer>
      {valueConvModals}
    </>
  );
};

export default DealConnectionsDrawer;
